import type { ApiProfile } from '../schemas/profile'
import type { TreeNode } from '../schemas/tree-node'
import type { FilterEntityFields, RightValue } from '../schemas/filter-entity'
import { createDefaultEntity } from '../schemas/filter-entity'
import type { TreeState } from '../reducer/tree-reducer'
import type { QueryFilterGroup } from '@/services/api/scanner-types'

/**
 * Convert an array [low, high] back to a { low, high } object for the `between` operator.
 * profileToQuery serializes range values as arrays for the API; this reverses that.
 */
function normalizeRangeValue(value: RightValue, operator: string): RightValue {
  if (
    operator === 'between' &&
    Array.isArray(value) &&
    value.length === 2 &&
    (typeof value[0] === 'number' || typeof value[0] === 'string') &&
    (typeof value[1] === 'number' || typeof value[1] === 'string')
  ) {
    return { low: value[0], high: value[1] }
  }
  return value
}

interface NormalizedTree {
  nodes: Record<string, TreeNode>
  entities: Record<string, FilterEntityFields>
  rootId: string
}

let nextId = 1
function genId(): string {
  return `node_${nextId++}`
}

/** Reset ID counter (for testing). */
export function resetIdCounter(): void {
  nextId = 1
}

/**
 * Convert an API profile's filter array into the normalized tree + entities
 * used by the tree reducer.
 *
 * Supports both formats:
 *   - Backend format: `[{ AND: [...] }]` or `[{ OR: [...] }]`
 *   - Legacy format: `[{ type: "and", children: [...] }]` or flat leaf objects
 */
export function profileToForm(profile: ApiProfile): NormalizedTree {
  const nodes: Record<string, TreeNode> = {}
  const entities: Record<string, FilterEntityFields> = {}

  // If filters is a single AND/OR group, unwrap it to avoid double-nesting.
  // formToProfile() produces [{ AND: [...] }], so we detect that pattern.
  const filters = profile.filters
  const firstFilter = filters.length === 1 ? (filters[0] as Record<string, unknown>) : null
  const isSingleGroup = firstFilter && ('AND' in firstFilter || 'OR' in firstFilter)

  if (isSingleGroup) {
    // Use the group directly as the top-level group (no extra AND wrapper)
    const groupType = 'OR' in firstFilter ? 'OR' : 'AND'
    const children = (firstFilter[groupType] as Record<string, unknown>[]) ?? []

    const groupId = genId()
    nodes[groupId] = { id: groupId, type: groupType as 'AND' | 'OR', parentId: null, children: [] }

    for (const child of children) {
      parseFilterNode(child, groupId, nodes, entities)
    }

    // Wrap in ROOT node
    const rootId = genId()
    nodes[rootId] = { id: rootId, type: 'ROOT', parentId: null, children: [groupId] }
    ;(nodes[groupId] as { parentId: string | null }).parentId = rootId

    return { nodes, entities, rootId }
  }

  // Multiple top-level items or legacy format — wrap in AND group
  const andId = genId()
  nodes[andId] = { id: andId, type: 'AND', parentId: null, children: [] }

  for (const filter of filters) {
    const f = filter as Record<string, unknown>
    parseFilterNode(f, andId, nodes, entities)
  }

  // Wrap in ROOT node
  const rootId = genId()
  nodes[rootId] = { id: rootId, type: 'ROOT', parentId: null, children: [andId] }
  ;(nodes[andId] as { parentId: string | null }).parentId = rootId

  return { nodes, entities, rootId }
}

function parseFilterNode(
  raw: Record<string, unknown>,
  parentId: string,
  nodes: Record<string, TreeNode>,
  entities: Record<string, FilterEntityFields>,
): void {
  // ─── Backend format: { AND: [...] } or { OR: [...] } ───
  if ('AND' in raw || 'OR' in raw) {
    const groupType = 'AND' in raw ? 'AND' : 'OR'
    const children = raw[groupType] as Record<string, unknown>[] | undefined

    const groupId = genId()
    nodes[groupId] = {
      id: groupId,
      type: groupType as 'AND' | 'OR',
      parentId,
      children: [],
    }

    const parent = nodes[parentId]
    if ('children' in parent) {
      parent.children.push(groupId)
    }

    if (children) {
      for (const child of children) {
        parseFilterNode(child, groupId, nodes, entities)
      }
    }
    return
  }

  // ─── Legacy format: { type: "and"/"or", children: [...] } ───
  const type = (raw.type as string)?.toUpperCase()

  if (type === 'AND' || type === 'OR') {
    const groupId = genId()
    nodes[groupId] = {
      id: groupId,
      type: type as 'AND' | 'OR',
      parentId,
      children: [],
    }

    const parent = nodes[parentId]
    if ('children' in parent) {
      parent.children.push(groupId)
    }

    const children = raw.children as Record<string, unknown>[] | undefined
    if (children) {
      for (const child of children) {
        parseFilterNode(child, groupId, nodes, entities)
      }
    }
    return
  }

  // ─── Leaf filter ───
  // Supports three formats:
  //   1. Profile format:  { column, operator, value, rightType, enabled, ... }
  //   2. Query format:    { left: { column }, operator, right: { value } }
  //   3. Legacy flat:     { column, operator, value }
  const filterId = genId()
  nodes[filterId] = { id: filterId, type: 'FILTER', parentId }

  // Detect query format (left/right binary args) vs flat format
  const left = raw.left as Record<string, unknown> | undefined
  const column = (left?.column as string | undefined) ?? (raw.column as string)
  const entity = createDefaultEntity(column)
  entity.operator = (raw.operator as FilterEntityFields['operator']) ?? 'gt'
  entity.enabled = raw.enabled !== false
  // Both stored and query shapes carry `allowNull` at the leaf level.
  if (raw.allowNull === true) entity.allowNull = true

  // Restore time slices from profile
  if (raw.rightType === 'time_slices' && Array.isArray(raw.timeSlices)) {
    entity.right = raw.timeSlices as FilterEntityFields['right']
    entity.rightType = 'time_slices'
  } else if (left) {
    // Query format: extract value from right.value or right.column
    const right = raw.right as Record<string, unknown> | undefined
    if (right && 'column' in right && typeof right.column === 'string') {
      entity.right = right.column
      entity.rightType = 'column'
    } else {
      const val = (right?.value as FilterEntityFields['right']) ?? null
      entity.right = val != null ? normalizeRangeValue(val, entity.operator) : null
      entity.rightType = (raw.rightType as FilterEntityFields['rightType']) ?? 'value'
    }
  } else {
    const val = (raw.value as FilterEntityFields['right']) ?? null
    entity.right = val != null ? normalizeRangeValue(val, entity.operator) : null
    entity.rightType = (raw.rightType as FilterEntityFields['rightType']) ?? 'value'
  }

  // Restore timeframe/aggregate from profile format
  if (raw.timeframe) entity.left.timeframe = raw.timeframe as string
  if (raw.aggregate) entity.left.aggregate = raw.aggregate as string

  entities[filterId] = entity

  const parent = nodes[parentId]
  if ('children' in parent) {
    parent.children.push(filterId)
  }
}

/**
 * Create an empty form: a single root AND group with NO filter rows.
 *
 * Deliberately does not seed an empty filter row (changed 2026-08-20): the
 * seeded row mounted an incomplete "select column > value" editor (and
 * auto-opened its column picker) every time a filterless form opened. The
 * empty group instead renders an "Add your first condition" CTA (TreeRenderer),
 * and rows only ever mount from an explicit add: which is also the only
 * moment the column picker is highlighted (it no longer auto-opens, 2026-10-01).
 */
export function createEmptyForm(): NormalizedTree {
  const rootId = genId()
  const andId = genId()

  const nodes: Record<string, TreeNode> = {
    [rootId]: { id: rootId, type: 'ROOT', parentId: null, children: [andId] },
    [andId]: { id: andId, type: 'AND', parentId: rootId, children: [] },
  }

  return { nodes, entities: {}, rootId }
}

/**
 * Detect whether a value is a nested QueryFilterGroup object
 * (has AND or OR keys with array values).
 */
function isFilterGroup(value: unknown): value is QueryFilterGroup {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false
  const obj = value as Record<string, unknown>
  return ('AND' in obj && Array.isArray(obj.AND)) || ('OR' in obj && Array.isArray(obj.OR))
}

/**
 * Convert saved filters back into TreeState for the filter form.
 *
 * Accepts three formats:
 *   - **Profile shape** `[{AND: [...profile leaves...]}]` — what `formToProfile`
 *     emits. Leaves carry `rightType` and `timeSlices` so time-slice filters
 *     survive reopen. Route through `profileToForm`.
 *   - **Flat `ScannerFilter[]` array** (legacy panel state / IDB data) — each
 *     item is a plain leaf `{column, operator, value}`.
 *   - **Nested `QueryFilterGroup`** object (`{AND: [...]}`) — what
 *     `profileToQuery` emits. Backend shape; loses time-slice metadata.
 */
export function filtersToTreeState(
  filters:
    | Array<{ column: string; operator: string; value?: unknown }>
    | Array<Record<string, unknown>>
    | QueryFilterGroup,
): TreeState {
  // ─── Nested QueryFilterGroup format ───
  if (isFilterGroup(filters)) {
    return filterGroupToTreeState(filters)
  }

  // ─── Array format — distinguish profile-shape (group wrapper) from flat ───
  const arr = filters as Array<Record<string, unknown>>

  if (arr.length === 0) {
    const empty = createEmptyForm()
    return {
      nodes: empty.nodes,
      entities: empty.entities,
      rootId: empty.rootId,
      activeProfileId: null,
      generation: 0,
    }
  }

  // Profile shape: first element is a group with `AND` or `OR`. Route through
  // `profileToForm` so `parseFilterNode`'s `rightType === 'time_slices'` branch
  // can restore slice arrays.
  const first = arr[0]
  if (first && typeof first === 'object' && ('AND' in first || 'OR' in first)) {
    const tree = profileToForm({ filters: arr } as never)
    return {
      nodes: tree.nodes,
      entities: tree.entities,
      rootId: tree.rootId,
      activeProfileId: null,
      generation: 0,
    }
  }

  // ─── Flat array format (legacy) ───
  const flat = arr as Array<{ column: string; operator: string; value?: unknown }>

  const rootId = genId()
  const andId = genId()

  const nodes: Record<string, TreeNode> = {
    [rootId]: { id: rootId, type: 'ROOT', parentId: null, children: [andId] },
    [andId]: { id: andId, type: 'AND', parentId: rootId, children: [] },
  }
  const entities: Record<string, FilterEntityFields> = {}

  for (const f of flat) {
    const filterId = genId()
    nodes[filterId] = { id: filterId, type: 'FILTER', parentId: andId }
    ;(nodes[andId] as { children: string[] }).children.push(filterId)

    const entity = createDefaultEntity(f.column)
    entity.operator = (f.operator as FilterEntityFields['operator']) ?? 'gt'
    const val = (f.value as FilterEntityFields['right']) ?? null
    entity.right = val != null ? normalizeRangeValue(val, entity.operator) : null
    entities[filterId] = entity
  }

  return {
    nodes,
    entities,
    rootId,
    activeProfileId: null,
    generation: 0,
  }
}

/**
 * Convert a nested QueryFilterGroup into TreeState.
 */
function filterGroupToTreeState(group: QueryFilterGroup): TreeState {
  const nodes: Record<string, TreeNode> = {}
  const entities: Record<string, FilterEntityFields> = {}

  const rootId = genId()
  const groupId = genId()

  const groupType = 'OR' in group && Array.isArray(group.OR) ? 'OR' : 'AND'
  const children = (group[groupType as keyof QueryFilterGroup] ?? []) as Record<string, unknown>[]

  nodes[rootId] = { id: rootId, type: 'ROOT', parentId: null, children: [groupId] }
  nodes[groupId] = { id: groupId, type: groupType as 'AND' | 'OR', parentId: rootId, children: [] }

  for (const child of children) {
    parseFilterNode(child, groupId, nodes, entities)
  }

  // Empty groups stay empty (changed 2026-08-20): the seeded template row
  // used to force an incomplete "select column" editor onto every filterless
  // load. TreeRenderer shows an "Add your first condition" CTA instead.

  return {
    nodes,
    entities,
    rootId,
    activeProfileId: null,
    generation: 0,
  }
}
